import { SpecialAttributeChip } from "@/components/special-attributes";
import { SalesOrderSheet } from "@/components/operations";
import { SalesOrderRateFields } from "@/components/operations/SalesOrderRateFields";
import { SuccessActionPanel } from "@/components/operations/SuccessActionPanel";
import {
  buildInventoryWorkspacePath,
  buildSupplyWorkspacePath,
  formatStatusLabel,
  formatUserDisplayName,
  getInventoryOperationalStatus,
  getSalesOrderErrorMessage,
  isInventoryBlockedForSalesOrderLinking,
  resolveProductCode,
  getSalesOrderOversellNotice,
  getSalesOrderShortfall,
  getSalesOrderShortfallFromError,
  SALES_ORDER_DRAFT_NOTICE,
  type SalesOrderShortfall,
} from "@/components/operations/operations-shared";
import { SalesOrderOversellDialog } from "@/components/operations/SalesOrderOversellDialog";
import { useUserStore } from "@/stores/user";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { exportSalesOrdersToCsv, exportSalesOrdersToExcel } from "@/utils/csv-export";
import { useProductsStore } from "@/stores/products";
import { useSpecialAttributes } from "@/hooks/repository-hooks/special-attribute/use-special-attribute";
import { SpecialAttribute } from "@/core/entities/special-attribute";
import {
  isInventoryAccountUnavailableForSale,
  type PurchaseOrder,
  type SalesOrder,
} from "@/core";
import {
  CalendarIcon,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchAllSalesOrders,
  useAllClients,
  useAllPurchaseOrders,
  useCreateSalesOrderManual,
  useDeals,
  useGetDealById,
  useInventories,
  useSalesOrders,
  useUsers,
} from "@/hooks";
import { Textarea } from "@/components/ui/textarea";
import { calculateOrderTotal, getSalesOrderTotal } from "@/utils/order-total";
import {
  buildDateRangeConstraints,
  buildExportSuffix,
  formatDateRangeLabel,
  getClientSearchKeywords,
  getClientSelectLabel,
} from "@/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const CREATE_MODE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "deal", label: "Source deal" },
] as const;

const INVENTORY_LINK_MODE_OPTIONS = [
  { value: "manual", label: "Manually choose inventory" },
  { value: "auto", label: "Auto-allocate with FIFO" },
] as const;

function formatShortId(value: string | undefined) {
  return value?.slice(0, 8) || "Unlinked";
}

function getSalesOrderCpm(totalMilesSold: number, revenue: number): number | null {
  if (!Number.isFinite(totalMilesSold) || totalMilesSold <= 0) {
    return null;
  }

  return (revenue / totalMilesSold) * 1000;
}

function getBookingConfirmationMailto(salesOrder: {
  id: string;
  clientEmail?: string;
  clientDisplayName?: string;
  totalMilesSold: number;
  revenue: number;
  taxCost?: number;
  totalAmount?: number;
}): string | null {
  if (!salesOrder.clientEmail) {
    return null;
  }

  const cpm = getSalesOrderCpm(salesOrder.totalMilesSold, salesOrder.revenue);
  const totalAmount = getSalesOrderTotal({
    revenue: salesOrder.revenue,
    taxCost: salesOrder.taxCost,
    totalAmount: salesOrder.totalAmount,
  });
  const bodyLines = [
    `Hi ${salesOrder.clientDisplayName || "there"},`,
    "",
    `Booking confirmation for Sales Order ${formatShortId(salesOrder.id)}:`,
    `- Miles used: ${salesOrder.totalMilesSold.toLocaleString()}`,
    `- CPM: ${cpm !== null ? cpm.toFixed(3) : "-"}`,
    `- Total: $${totalAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
    ...(typeof salesOrder.taxCost === "number" && salesOrder.taxCost > 0
      ? [`- Tax: $${salesOrder.taxCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}`]
      : []),
    "",
    "Please confirm everything looks correct on your side.",
  ];

  const subject = `Booking confirmation · SO ${formatShortId(salesOrder.id)}`;
  const body = bodyLines.join("\n");

  return `mailto:${encodeURIComponent(salesOrder.clientEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const getToday = () => new Date().toISOString().split("T")[0];

const SALES_ORDER_PAGE_SIZE = 100;

function formatDate(value: Date | undefined): string {
  if (!value) {
    return "—";
  }

  return value.toLocaleDateString(undefined, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
}

export default function SalesOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSoId = searchParams.get("soId") || "";
  const sourceDealIdFromSearch = searchParams.get("sourceDealId") || "";
  const createModeFromSearch = searchParams.get("createMode");

  const [createdDateRange, setCreatedDateRange] = useState<DateRange | undefined>();
  const createdRangeConstraints = useMemo(
    () => buildDateRangeConstraints<SalesOrder>(createdDateRange, "createdAt"),
    [createdDateRange],
  );
  const [awaitingApprovalOnly, setAwaitingApprovalOnly] = useState(false);
  const salesOrdersQuery = useSalesOrders({
    queryConstraints: awaitingApprovalOnly
      ? [
          ...createdRangeConstraints,
          { field: "status", operator: "==", value: "draft" },
        ]
      : createdRangeConstraints,
    pagination: { limit: SALES_ORDER_PAGE_SIZE },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  // Drafts are the admins' approval queue, so keep their count visible.
  const draftSalesOrdersQuery = useSalesOrders({
    queryConstraints: [{ field: "status", operator: "==", value: "draft" }],
    pagination: { limit: SALES_ORDER_PAGE_SIZE },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  const draftSalesOrderCount =
    draftSalesOrdersQuery.data?.pages.flat().length ?? 0;
  const specialAttributesQuery = useSpecialAttributes();
  const specialAttributeById = useMemo(() => {
    const map = new Map<string, SpecialAttribute>();
    for (const entry of specialAttributesQuery.data ?? []) map.set(entry.id, entry);
    return map;
  }, [specialAttributesQuery.data]);
  const products = useProductsStore((state) => state.products);
  const productCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const product of products) {
      if (product.id && product.productCode) {
        map.set(product.id, product.productCode);
      }
    }
    return map;
  }, [products]);
  const getSalesOrderProductCode = useMemo(
    () => (order: { productId: string; productName: string }) =>
      resolveProductCode({
        productCode: productCodeById.get(order.productId),
        productName: order.productName,
      }),
    [productCodeById],
  );
  const inventoriesQuery = useInventories({
    pagination: { limit: 100 },
    orderBy: { field: "updatedAt", direction: "desc" },
  });
  const clientsQuery = useAllClients();
  const usersQuery = useUsers();
  const sourceDealsQuery = useDeals({
    pagination: { limit: 100 },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  const createMutation = useCreateSalesOrderManual();
  const { isAdmin } = useUserStore();
  const canApprove = isAdmin();
  const [createError, setCreateError] = useState<string | null>(null);
  // Open by default; ops collapse it to give the sell orders table the full page.
  const [isCreateFormExpanded, setIsCreateFormExpanded] = useState(true);
  const toggleCreateForm = () => setIsCreateFormExpanded((current) => !current);
  const [oversellToConfirm, setOversellToConfirm] =
    useState<SalesOrderShortfall | null>(null);

  const [createMode, setCreateMode] = useState<"manual" | "deal">(
    createModeFromSearch === "deal" || sourceDealIdFromSearch ? "deal" : "manual",
  );
  const [sourceDealId, setSourceDealId] = useState(sourceDealIdFromSearch);
  const selectedSourceDealQuery = useGetDealById(sourceDealId);

  const [inventoryLinkMode, setInventoryLinkMode] = useState<"manual" | "auto">(
    "manual",
  );
  const [inventoryId, setInventoryId] = useState("");
  const [clientId, setClientId] = useState("");
  const [flightDate, setFlightDate] = useState("");
  const [soldByUserId, setSoldByUserId] = useState("");
  const [bookingEndDate, setBookingEndDate] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(getToday);
  const [totalMilesSold, setTotalMilesSold] = useState("");
  const [revenue, setRevenue] = useState("");
  const [taxCost, setTaxCost] = useState("");
  const [soNotes, setSoNotes] = useState("");
  const [selectedSalesOrderIds, setSelectedSalesOrderIds] = useState<Set<string>>(
    new Set(),
  );
  const [successState, setSuccessState] = useState<{
    title: string;
    description: string;
    details: string[];
    nextAction: string;
    actions: Array<{ label: string; href?: string; onClick?: () => void }>;
  } | null>(null);

  const salesOrders = useMemo(
    () => salesOrdersQuery.data?.pages.flat() ?? [],
    [salesOrdersQuery.data],
  );
  // A sell order has no direct PO reference — the only link is the shared
  // inventory, which may carry more than one purchase order.
  const { purchaseOrders: allPurchaseOrders, isLoading: purchaseOrdersLoading } =
    useAllPurchaseOrders();
  const purchaseOrdersByInventoryId = useMemo(() => {
    const map = new Map<string, PurchaseOrder[]>();
    for (const purchaseOrder of allPurchaseOrders) {
      if (!purchaseOrder.inventoryId) continue;
      const list = map.get(purchaseOrder.inventoryId);
      if (list) {
        list.push(purchaseOrder);
      } else {
        map.set(purchaseOrder.inventoryId, [purchaseOrder]);
      }
    }
    // Oldest first, so the original purchase leads.
    for (const list of map.values()) {
      list.sort(
        (a, b) => (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0),
      );
    }
    return map;
  }, [allPurchaseOrders]);
  const selectedSalesOrders = useMemo(
    () => salesOrders.filter((salesOrder) => selectedSalesOrderIds.has(salesOrder.id)),
    [salesOrders, selectedSalesOrderIds],
  );
  const allSalesOrdersSelected =
    salesOrders.length > 0 &&
    salesOrders.every((salesOrder) => selectedSalesOrderIds.has(salesOrder.id));
  const someSalesOrdersSelected =
    !allSalesOrdersSelected &&
    salesOrders.some((salesOrder) => selectedSalesOrderIds.has(salesOrder.id));
  const inventories = useMemo(
    () => inventoriesQuery.data?.pages.flat() ?? [],
    [inventoriesQuery.data],
  );
  const clients = useMemo(
    () => clientsQuery.data?.pages.flat() ?? [],
    [clientsQuery.data],
  );
  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const soldByOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: formatUserDisplayName(user.email),
        keywords: [formatUserDisplayName(user.email), user.email, user.id],
      })),
    [users],
  );
  const sourceDeals = useMemo(() => {
    const deals = sourceDealsQuery.data?.pages.flat() ?? [];
    return deals.filter((deal) => deal.buyOrSell?.toLowerCase() === "buy");
  }, [sourceDealsQuery.data]);

  const selectedSourceDeal = selectedSourceDealQuery.data;
  const availableInventories = useMemo(() => {
    const productFilteredInventories =
      createMode !== "deal" || !selectedSourceDeal?.productId
        ? inventories
        : inventories.filter(
            (inventory) => inventory.productId === selectedSourceDeal.productId,
          );

    return productFilteredInventories.filter(
      (inventory) =>
        !isInventoryBlockedForSalesOrderLinking({ inventory }) &&
        getInventoryOperationalStatus({ inventory }) !== "closed",
    );
  }, [createMode, inventories, selectedSourceDeal?.productId]);
  const selectedInventory = availableInventories.find(
    (inventory) => inventory.id === inventoryId,
  );

  const totalMilesSoldValue = Number(totalMilesSold);
  const shortfall = selectedInventory
    ? getSalesOrderShortfall({
        requiredMiles: totalMilesSoldValue || 0,
        availableMiles: selectedInventory.availableMiles,
      })
    : null;
  // Only an oversell needs approval.
  const createsDraft = !canApprove && shortfall !== null;
  const revenueValue = Number(revenue);
  const taxCostValue = Number(taxCost);
  const totalAmountValue =
    Number.isFinite(revenueValue) && Number.isFinite(taxCostValue)
      ? calculateOrderTotal({
        amount: revenueValue,
        taxCost: taxCostValue,
      })
      : 0;
  const autoAllocationSummary = useMemo(() => {
    if (createMode !== "deal" || !selectedSourceDeal?.productId) {
      return null;
    }

    const eligibleInventories = inventories.filter(
      (inventory) =>
        inventory.productId === selectedSourceDeal.productId &&
        !isInventoryAccountUnavailableForSale(inventory.accountStatus) &&
        !isInventoryBlockedForSalesOrderLinking({ inventory }),
    );
    const totalEligibleMiles = eligibleInventories.reduce((sum, inventory) => {
      return sum + Math.max(0, inventory.availableMiles);
    }, 0);
    const shortfallMiles =
      totalMilesSoldValue > 0
        ? Math.max(0, totalMilesSoldValue - totalEligibleMiles)
        : 0;

    return {
      inventoryCount: eligibleInventories.length,
      totalEligibleMiles,
      shortfallMiles,
    };
  }, [
    createMode,
    inventories,
    selectedSourceDeal?.productId,
    totalMilesSoldValue,
  ]);

  useEffect(() => {
    if (!sourceDealIdFromSearch) {
      return;
    }

    setCreateMode("deal");
    setSourceDealId(sourceDealIdFromSearch);
  }, [sourceDealIdFromSearch]);

  // Prefill only when a different source deal is picked, so refetches of the
  // same deal don't overwrite what the user has typed.
  const prefilledSourceDealIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!selectedSourceDeal) {
      prefilledSourceDealIdRef.current = undefined;
      return;
    }
    if (prefilledSourceDealIdRef.current === selectedSourceDeal.id) {
      return;
    }
    prefilledSourceDealIdRef.current = selectedSourceDeal.id;

    setTotalMilesSold(String(selectedSourceDeal.quantity));
    setRevenue(String(selectedSourceDeal.amount));
  }, [selectedSourceDeal]);

  const canCreateSo =
    Boolean(flightDate) &&
    Boolean(bookingEndDate) &&
    Boolean(soldByUserId) &&
    Number.isFinite(totalMilesSoldValue) &&
    totalMilesSoldValue > 0 &&
    Number.isFinite(revenueValue) &&
    revenueValue >= 0 &&
    Number.isFinite(taxCostValue) &&
    taxCostValue >= 0 &&
    (createMode === "manual"
      ? Boolean(inventoryId) && Boolean(clientId)
      : Boolean(sourceDealId) &&
        (inventoryLinkMode === "manual" ? Boolean(inventoryId) : true));

  const handleSourceDealChange = (nextSourceDealId: string) => {
    setSourceDealId(nextSourceDealId);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);

      if (nextSourceDealId) {
        next.set("createMode", "deal");
        next.set("sourceDealId", nextSourceDealId);
      } else {
        next.delete("createMode");
        next.delete("sourceDealId");
      }

      return next;
    });
  };

  const handleCreateModeChange = (nextMode: "manual" | "deal") => {
    setCreateMode(nextMode);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);

      if (nextMode === "deal") {
        next.set("createMode", "deal");
        if (sourceDealId) {
          next.set("sourceDealId", sourceDealId);
        }
      } else {
        next.delete("createMode");
        next.delete("sourceDealId");
      }

      return next;
    });
  };

  const toggleSalesOrderSelection = (salesOrderId: string, checked: boolean) => {
    setSelectedSalesOrderIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(salesOrderId);
      } else {
        next.delete(salesOrderId);
      }
      return next;
    });
  };

  const [exportPending, setExportPending] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  /**
   * Exports every sell order in the selected date window — not just the rows
   * already paged into the table.
   */
  const handleExportRange = async (fileFormat: "csv" | "excel") => {
    setExportPending(true);
    setExportMessage(null);

    try {
      const orders = await fetchAllSalesOrders(createdRangeConstraints);

      if (orders.length === 0) {
        setExportMessage("No sell orders found for the selected date range.");
        return;
      }

      const suffix = buildExportSuffix(createdDateRange);

      if (fileFormat === "csv") {
        exportSalesOrdersToCsv(
          orders,
          productCodeById,
          `sales-orders-${suffix}.csv`,
          purchaseOrdersByInventoryId,
        );
      } else {
        exportSalesOrdersToExcel(
          orders,
          productCodeById,
          `sales-orders-${suffix}.xlsx`,
          purchaseOrdersByInventoryId,
        );
      }

      setExportMessage(
        `Exported ${orders.length.toLocaleString()} sell order${orders.length === 1 ? "" : "s"} (${formatDateRangeLabel(createdDateRange)}).`,
      );
    } catch (error) {
      setExportMessage(
        error instanceof Error
          ? `Export failed: ${error.message}`
          : "Export failed. Please try again.",
      );
    } finally {
      setExportPending(false);
    }
  };

  const handleExportSelected = (fileFormat: "csv" | "excel") => {
    const suffix = buildExportSuffix(createdDateRange);

    if (fileFormat === "csv") {
      exportSalesOrdersToCsv(
        selectedSalesOrders,
        productCodeById,
        `sales-orders-selected-${suffix}.csv`,
        purchaseOrdersByInventoryId,
      );
    } else {
      exportSalesOrdersToExcel(
        selectedSalesOrders,
        productCodeById,
        `sales-orders-selected-${suffix}.xlsx`,
        purchaseOrdersByInventoryId,
      );
    }
  };

  const toggleAllSalesOrdersSelection = (checked: boolean) => {
    setSelectedSalesOrderIds((previous) => {
      const next = new Set(previous);

      salesOrders.forEach((salesOrder) => {
        if (checked) {
          next.add(salesOrder.id);
        } else {
          next.delete(salesOrder.id);
        }
      });

      return next;
    });
  };

  const handleCreate = async (allowOversell = false) => {
    if (!canCreateSo) {
      return;
    }

    setCreateError(null);

    if (canApprove && shortfall && !allowOversell) {
      setOversellToConfirm(shortfall);
      return;
    }

    let result: Awaited<ReturnType<typeof createMutation.mutateAsync>>;

    try {
      result = await createMutation.mutateAsync(
        createMode === "manual"
          ? {
              createMode: "manual",
              allowOversell: canApprove && allowOversell,
              inventoryId,
              flightDate,
              soldByUserId,
              bookingEndDate,
              purchaseDate: purchaseDate || undefined,
              clientId,
              productId: selectedInventory?.productId || "",
              productName: selectedInventory?.productName || "",
              totalMilesSold: totalMilesSoldValue,
              revenue: revenueValue,
              taxCost: taxCostValue,
              salesChannel: "manual",
              notes: soNotes.trim() || undefined,
            }
          : {
              createMode: "deal",
              allowOversell: canApprove && allowOversell,
              inventoryLinkMode,
              inventoryId: inventoryLinkMode === "manual" ? inventoryId : undefined,
              flightDate,
              soldByUserId,
              bookingEndDate,
              purchaseDate: purchaseDate || undefined,
              sourceDealId,
              totalMilesSold: totalMilesSoldValue,
              revenue: revenueValue,
              taxCost: taxCostValue,
              salesChannel: "hubspot",
              notes: soNotes.trim() || undefined,
            },
      );
    } catch (error: unknown) {
      // The miles may have changed since the page loaded; let an admin decide.
      const serverShortfall = getSalesOrderShortfallFromError(error);
      if (canApprove && serverShortfall && !allowOversell) {
        setOversellToConfirm(serverShortfall);
        return;
      }
      setCreateError(getSalesOrderErrorMessage(error));
      return;
    }

    const isDraft = result.status === "draft";

    setFlightDate("");
    setBookingEndDate("");
    setPurchaseDate(getToday());
    setTotalMilesSold("");
    setRevenue("");
    setTaxCost("");
    setSoNotes("");
    setSourceDealId("");
    setSuccessState({
      title: isDraft ? "Sell order submitted for approval" : "Sell order created",
      description: isDraft
        ? SALES_ORDER_DRAFT_NOTICE
        : createMode === "deal"
          ? "The sell order was created from the selected source deal and linked to inventory."
          : "The sell order is linked directly to an inventory-backed account.",
      details: [
        `Sell Order ID: ${result.soId}`,
        `Inventory ID: ${result.inventoryId}`,
        ...(createMode === "deal" ? [`Source Deal ID: ${sourceDealId}`] : []),
      ],
      nextAction: "Continue inside the inventory workspace to manage sell orders and receivables.",
      actions: [
        {
          label: "Open SO",
          onClick: () => setSearchParams({ soId: result.soId }, { replace: true }),
        },
        {
          label: "Open Inventory Workspace",
          href: buildInventoryWorkspacePath({
            inventoryId: result.inventoryId,
            salesOrderId: result.soId,
            focus: "sell-orders",
          }),
        },
      ],
    });
    setSearchParams({ soId: result.soId }, { replace: true });
  };

  return (
    <div
      className={cn(
        "container mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8",
        isCreateFormExpanded
          ? "min-h-[calc(100svh-var(--header-height)-1rem)]"
          : "h-[calc(100svh-var(--header-height)-1rem)] overflow-hidden",
      )}
    >
      {successState ? (
        <SuccessActionPanel
          title={successState.title}
          description={successState.description}
          details={successState.details}
          nextAction={successState.nextAction}
          actions={successState.actions}
          onDismiss={() => setSuccessState(null)}
        />
      ) : null}

      <Card className="shrink-0">
        <CardHeader
          role="button"
          tabIndex={0}
          aria-expanded={isCreateFormExpanded}
          aria-controls="create-sell-order-form"
          className="cursor-pointer select-none"
          onClick={toggleCreateForm}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleCreateForm();
            }
          }}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1.5">
              <CardTitle>Create Sell Order</CardTitle>
              <CardDescription>
                Create a sell order manually or directly from a source deal. Source-deal
                orders can either be linked manually to an inventory or auto-allocated with
                FIFO on the server.
              </CardDescription>
            </div>
            {isCreateFormExpanded ? (
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        {isCreateFormExpanded ? (
          <CardContent
            id="create-sell-order-form"
            className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
          >
            <div className="space-y-1">
              <Label>Create Mode</Label>
              <Select
                value={createMode}
                onValueChange={(value: "manual" | "deal") => handleCreateModeChange(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREATE_MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {createMode === "deal" ? (
              <div className="space-y-1 md:col-span-2 xl:col-span-3">
                <Label>Source Deal</Label>
                <SearchableSelect
                  value={sourceDealId}
                  onChange={handleSourceDealChange}
                  placeholder="Select source deal"
                  searchPlaceholder="Search source deal..."
                  emptyText="No matching source deals found."
                  options={sourceDeals.map((deal) => ({
                    value: deal.id,
                    label: `${deal.name} · ${deal.productName} · ${deal.quantity.toLocaleString()} miles`,
                    keywords: [deal.name, deal.productName, deal.id],
                  }))}
                />
              </div>
            ) : null}

            {createMode === "deal" && sourceDealId ? (
              <div className="rounded-lg border bg-muted/20 p-4 md:col-span-2 xl:col-span-4">
                {selectedSourceDealQuery.isLoading ? (
                  <div className="text-sm text-muted-foreground">
                    Loading source deal context...
                  </div>
                ) : selectedSourceDeal ? (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Deal
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        {selectedSourceDeal.name}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Product
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        {selectedSourceDeal.productName}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Quantity
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        {selectedSourceDeal.quantity.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Amount
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        ${selectedSourceDeal.amount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-destructive">
                    The selected source deal could not be loaded.
                  </div>
                )}
              </div>
            ) : null}

            {createMode === "deal" ? (
              <div className="space-y-1">
                <Label>Inventory Linking</Label>
                <Select
                  value={inventoryLinkMode}
                  onValueChange={(value: "manual" | "auto") => setInventoryLinkMode(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_LINK_MODE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {(createMode === "manual" || inventoryLinkMode === "manual") ? (
              <div className="space-y-1">
                <Label>Inventory</Label>
                <SearchableSelect
                  value={inventoryId}
                  onChange={setInventoryId}
                  placeholder="Select inventory"
                  searchPlaceholder="Search inventory..."
                  emptyText="No inventories found."
                  options={availableInventories.map((inventory) => ({
                    value: inventory.id,
                    label: `${inventory.name} · ${inventory.productName}`,
                  }))}
                />
              </div>
            ) : (
              <div className="rounded-lg border bg-muted/20 p-4 md:col-span-2 xl:col-span-3">
                <div className="text-sm font-medium">FIFO auto-allocation</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  The server will resolve the oldest eligible inventory for this product. If
                  there is not enough inventory, creation will fail with a business error.
                </div>
                {autoAllocationSummary ? (
                  <div className="mt-3 flex flex-wrap gap-4 text-sm">
                    <span>
                      Eligible inventories:{" "}
                      {autoAllocationSummary.inventoryCount.toLocaleString()}
                    </span>
                    <span>
                      Eligible miles:{" "}
                      {autoAllocationSummary.totalEligibleMiles.toLocaleString()}
                    </span>
                    <span>
                      Shortfall: {autoAllocationSummary.shortfallMiles.toLocaleString()}
                    </span>
                  </div>
                ) : null}
              </div>
            )}

            {createMode === "manual" ? (
              <div className="space-y-1">
                <Label>Client</Label>
                <SearchableSelect
                  value={clientId}
                  onChange={setClientId}
                  placeholder="Select client"
                  searchPlaceholder="Search client..."
                  emptyText="No clients found."
                  options={clients.map((client) => ({
                    value: client.id,
                    label: getClientSelectLabel({ client }),
                    keywords: getClientSearchKeywords({ client }),
                  }))}
                />
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Client</Label>
                <Input value={selectedSourceDeal?.name || "Derived from source deal"} disabled />
              </div>
            )}

            <div className="space-y-1">
              <Label>Order Date</Label>
              <Input
                type="date"
                value={purchaseDate}
                onChange={(event) => setPurchaseDate(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label>Flight Date</Label>
              <Input
                type="date"
                value={flightDate}
                onChange={(event) => setFlightDate(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label>Booking End Date</Label>
              <Input
                type="date"
                value={bookingEndDate}
                onChange={(event) => setBookingEndDate(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label>Sold By</Label>
              <SearchableSelect
                value={soldByUserId}
                onChange={setSoldByUserId}
                placeholder="Select sold-by name"
                searchPlaceholder="Search sold-by name..."
                emptyText="No users found."
                options={soldByOptions}
              />
            </div>

            <div className="space-y-1">
              <Label>Product</Label>
              <Input
                value={
                  createMode === "manual"
                    ? selectedInventory?.productName || ""
                    : selectedSourceDeal?.productName || ""
                }
                disabled
              />
            </div>

            <div className="space-y-1">
              <Label>Miles Sold</Label>
              <Input
                value={totalMilesSold}
                onChange={(event) => setTotalMilesSold(event.target.value)}
                placeholder="30000"
              />
            </div>

            <SalesOrderRateFields
              milesSold={totalMilesSold}
              revenue={revenue}
              onRevenueChange={setRevenue}
            />

            <div className="space-y-1">
              <Label>Tax Cost</Label>
              <Input
                value={taxCost}
                onChange={(event) => setTaxCost(event.target.value)}
                placeholder="0"
              />
            </div>

            <div className="space-y-1">
              <Label>Total Amount</Label>
              <Input value={String(totalAmountValue)} disabled />
            </div>

            <div className="space-y-1 md:col-span-2 xl:col-span-4">
              <Label>Notes</Label>
              <Textarea
                value={soNotes}
                onChange={(event) => setSoNotes(event.target.value)}
                placeholder="Operational notes"
                rows={3}
              />
            </div>

            {createError ? (
              <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 md:col-span-2 xl:col-span-4">
                {createError}
              </div>
            ) : null}

            {shortfall ? (
              <div className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 md:col-span-2 xl:col-span-4">
                {getSalesOrderOversellNotice({ shortfall, canApprove })}
              </div>
            ) : null}

            <SalesOrderOversellDialog
              shortfall={oversellToConfirm}
              onCancel={() => setOversellToConfirm(null)}
              onConfirm={() => {
                setOversellToConfirm(null);
                void handleCreate(true);
              }}
            />

            <div className="flex justify-end md:col-span-2 xl:col-span-4">
              <Button
                onClick={() => void handleCreate()}
                disabled={!canCreateSo}
                loading={createMutation.isPending}
                loadingText="Creating..."
              >
                {createsDraft
                  ? "Submit for Approval"
                  : createMode === "deal"
                    ? "Create Sell Order from Source Deal"
                    : "Create Sell Order"}
              </Button>
            </div>
          </CardContent>
        ) : null}
      </Card>

      <Card
        className={cn(
          "flex flex-col",
          isCreateFormExpanded ? "" : "min-h-0 flex-1",
        )}
      >
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Sell Orders</CardTitle>
            <CardDescription>
              Sell orders remain list views and route into the linked inventory workspace.
              Pick a created-date range to narrow the view — exports always cover the whole
              range, not just the rows loaded below.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={awaitingApprovalOnly ? "default" : "outline"}
              size="sm"
              aria-pressed={awaitingApprovalOnly}
              onClick={() => setAwaitingApprovalOnly((current) => !current)}
            >
              Awaiting Approval
              {draftSalesOrderCount > 0
                ? ` (${draftSalesOrderCount}${draftSalesOrdersQuery.hasNextPage ? "+" : ""})`
                : ""}
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-w-0 justify-start overflow-hidden text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                  <span className="truncate">
                    {formatDateRangeLabel(createdDateRange)}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={createdDateRange}
                  onSelect={setCreatedDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {createdDateRange?.from ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCreatedDateRange(undefined)}
              >
                <X className="mr-2 h-4 w-4" />
                Clear
              </Button>
            ) : null}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={exportPending}>
                  {exportPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {exportPending ? "Exporting..." : "Export"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={selectedSalesOrders.length === 0}
                  onClick={() => handleExportSelected("csv")}
                >
                  Export Selected CSV ({selectedSalesOrders.length})
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={selectedSalesOrders.length === 0}
                  onClick={() => handleExportSelected("excel")}
                >
                  Export Selected Excel ({selectedSalesOrders.length})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportRange("csv")}>
                  {createdDateRange?.from
                    ? "Export CSV — all in date range"
                    : "Export CSV — all sell orders"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportRange("excel")}>
                  {createdDateRange?.from
                    ? "Export Excel — all in date range"
                    : "Export Excel — all sell orders"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent
          className={cn(
            "flex flex-col",
            isCreateFormExpanded ? "" : "min-h-0 flex-1",
          )}
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>
              Showing {salesOrders.length.toLocaleString()} sell order
              {salesOrders.length === 1 ? "" : "s"}
              {salesOrdersQuery.hasNextPage ? " (more available)" : ""} ·{" "}
              {awaitingApprovalOnly ? "Awaiting approval · " : ""}
              {formatDateRangeLabel(createdDateRange)}
              {selectedSalesOrders.length > 0
                ? ` · ${selectedSalesOrders.length.toLocaleString()} selected`
                : ""}
            </span>
            {exportMessage ? (
              <span className="text-foreground">{exportMessage}</span>
            ) : null}
          </div>
          <Table
            containerClassName={cn(
              "overflow-auto",
              isCreateFormExpanded ? "max-h-[70vh]" : "min-h-0 flex-1",
            )}
          >
            <TableHeader className="sticky top-0 z-10 bg-card [&_th]:shadow-[inset_0_-1px_0_var(--color-border)]">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Select all sales orders"
                    checked={
                      allSalesOrdersSelected
                        ? true
                        : someSalesOrdersSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(checked) =>
                      toggleAllSalesOrdersSelection(checked === true)
                    }
                    onClick={(event) => event.stopPropagation()}
                  />
                </TableHead>
                <TableHead>Created</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Inventory</TableHead>
                <TableHead>Purchase Order</TableHead>
                <TableHead>Product Code</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attributes</TableHead>
                <TableHead className="text-right">Miles</TableHead>
                <TableHead className="text-right">Miles Amount</TableHead>
                <TableHead className="text-right">CPM</TableHead>
                <TableHead className="text-right">Tax</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {salesOrdersQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={15} className="text-center text-muted-foreground">
                    Loading sell orders...
                  </TableCell>
                </TableRow>
              ) : salesOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} className="text-center text-muted-foreground">
                    No sell orders found.
                  </TableCell>
                </TableRow>
              ) : (
                salesOrders.map((salesOrder) => (
                  <TableRow
                    key={salesOrder.id}
                    className="cursor-pointer"
                    onClick={() => setSearchParams({ soId: salesOrder.id }, { replace: true })}
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        aria-label={`Select sales order ${salesOrder.id.slice(0, 8)}`}
                        checked={selectedSalesOrderIds.has(salesOrder.id)}
                        onCheckedChange={(checked) =>
                          toggleSalesOrderSelection(salesOrder.id, checked === true)
                        }
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(salesOrder.createdAt)}
                    </TableCell>
                    <TableCell className="font-mono text-xs" title={salesOrder.id}>
                      {formatShortId(salesOrder.id)}
                    </TableCell>
                    <TableCell>{formatShortId(salesOrder.inventoryId)}</TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <SalesOrderPurchaseOrdersCell
                        inventoryId={salesOrder.inventoryId}
                        purchaseOrders={
                          purchaseOrdersByInventoryId.get(salesOrder.inventoryId) ?? []
                        }
                        isLoading={purchaseOrdersLoading}
                      />
                    </TableCell>
                    <TableCell>{getSalesOrderProductCode(salesOrder)}</TableCell>
                    <TableCell>
                      {salesOrder.clientDisplayName ||
                        salesOrder.clientEmail ||
                        "Unknown client"}
                    </TableCell>
                    <TableCell>{formatStatusLabel(salesOrder.status)}</TableCell>
                    <TableCell>
                      <SalesOrderAttributesCell
                        ids={salesOrder.specialAttributeIds ?? []}
                        poolById={specialAttributeById}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {salesOrder.totalMilesSold.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      ${salesOrder.revenue.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {salesOrder.totalMilesSold > 0
                        ? `${((salesOrder.revenue / salesOrder.totalMilesSold) * 100).toFixed(2)}¢/mi`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      ${(salesOrder.taxCost ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      ${getSalesOrderTotal({
                        revenue: salesOrder.revenue,
                        taxCost: salesOrder.taxCost,
                        totalAmount: salesOrder.totalAmount,
                      }).toLocaleString()}
                    </TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            Actions
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link
                              to={buildInventoryWorkspacePath({
                                inventoryId: salesOrder.inventoryId,
                                salesOrderId: salesOrder.id,
                                focus: "sell-orders",
                              })}
                            >
                              Workspace
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!salesOrder.clientEmail}
                            onClick={() => {
                              const mailto = getBookingConfirmationMailto({
                                id: salesOrder.id,
                                clientEmail: salesOrder.clientEmail,
                                clientDisplayName: salesOrder.clientDisplayName,
                                totalMilesSold: salesOrder.totalMilesSold,
                                revenue: salesOrder.revenue,
                                taxCost: salesOrder.taxCost,
                                totalAmount: salesOrder.totalAmount,
                              });

                              if (!mailto) {
                                return;
                              }

                              window.location.href = mailto;
                            }}
                          >
                            Send booking confirmation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {salesOrdersQuery.hasNextPage ? (
            <div className="mt-4 flex shrink-0 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => salesOrdersQuery.fetchNextPage()}
                loading={salesOrdersQuery.isFetchingNextPage}
                loadingText="Loading..."
              >
                Load more
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <SalesOrderSheet
        soId={selectedSoId}
        open={selectedSoId.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              next.delete("soId");
              return next;
            }, { replace: true });
          }
        }}
      />
    </div>
  );
}

/**
 * Purchase orders linked to a sell order through its inventory.
 *
 * Most inventories carry exactly one PO, but ~20% carry several — those are all
 * listed rather than silently picking one, since nothing in the data says which
 * PO's miles a given sell order consumed.
 */
function SalesOrderPurchaseOrdersCell({
  inventoryId,
  purchaseOrders,
  isLoading,
}: Readonly<{
  inventoryId: string;
  purchaseOrders: PurchaseOrder[];
  isLoading: boolean;
}>) {
  if (purchaseOrders.length === 0) {
    return (
      <span className="text-muted-foreground">{isLoading ? "…" : "—"}</span>
    );
  }

  const [primary, ...rest] = purchaseOrders;
  const tooltip = purchaseOrders
    .map(
      (purchaseOrder) =>
        `${formatShortId(purchaseOrder.id)} · ${purchaseOrder.vendorDisplayName || purchaseOrder.vendorEmail || "Unknown vendor"} · ${purchaseOrder.amountBought.toLocaleString()} miles`,
    )
    .join("\n");

  return (
    <div className="flex items-center gap-1 whitespace-nowrap" title={tooltip}>
      <Link
        className="underline underline-offset-2"
        to={buildSupplyWorkspacePath({ inventoryId, poId: primary.id })}
      >
        {formatShortId(primary.id)}
      </Link>
      {rest.length > 0 ? (
        <span className="text-xs text-muted-foreground">+{rest.length}</span>
      ) : null}
    </div>
  );
}

function SalesOrderAttributesCell({
  ids,
  poolById,
}: Readonly<{
  ids: string[];
  poolById: Map<string, SpecialAttribute>;
}>) {
  if (ids.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const visible = ids.slice(0, 3);
  const overflow = ids.length - visible.length;
  return (
    <div
      className="flex flex-wrap gap-1"
      title={ids.map((id) => poolById.get(id)?.name ?? id).join(", ")}
    >
      {visible.map((id) => {
        const entry = poolById.get(id);
        return (
          <SpecialAttributeChip
            key={id}
            name={entry?.name ?? id}
            color={entry?.color}

            className="px-2 py-0.5 text-xs"
          />
        );
      })}
      {overflow > 0 ? (
        <span className="text-xs text-muted-foreground">+{overflow}</span>
      ) : null}
    </div>
  );
}
